import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import {
  FormGroup,
  FormSelect,
  FormHelpText,
  Type,
  ButtonTertiary,
  IconPlusAlt,
} from '@spotify-internal/encore-creator-web';
import { get, filter, isEmpty } from 'lodash';
import { Category } from './Category';
import { trackEvent } from '../../../lib/Tracking';
import { DEFAULT_SPOTIFY_AGGREGATOR_ID } from '../../../lib/constants';
import {
  Category as CategoryType,
  SelectedCategory,
  Country,
  Language,
  Network,
  Subcategory,
} from '../../../types';
import { PodcastModalBody } from '../../Shared/PodcastModal';
import { FormattedMessage, defineMessages, IntlShape } from 'react-intl';

const AdditionalCategoryButton = styled(ButtonTertiary)`
  display: flex;
  align-items: center;
  padding-left: 0;
  padding-top: 0;
  text-transform: initial;
  letter-spacing: unset;

  & > p {
    margin-left: 8px;
  }
`;

interface Props {
  countries: Country[];
  categories: CategoryType[];
  languages: Language[];
  aggregators: Network[];

  onChange(field: string): (event: ChangeEvent<HTMLSelectElement>) => void;

  selectedLanguage?: Language;
  selectedCountry?: Country;
  selectedAggregator?: Network;
  selectedCategories?: SelectedCategory[];

  errors?: {
    country?: boolean;
    language?: boolean;
    category?: boolean[];
    aggregator?: boolean;
  };

  setCategory(key: string, category: { name: string; value: string }): void;
  setSubcategory(key: string, subcategory: Subcategory): void;
  removeCategory(key: string): void;
  removeSubcategory(key: string, subcategory: Subcategory): void;

  shouldShowAggregators?: boolean;

  initializeCategory(key?: string): void;
  initializeCountry(): void;
  initializeLanguage(): void;
  initializeAggregator(): void;
  intl: IntlShape;
}

export class AdditionalInfoForm extends React.Component<Props> {
  static defaultProps = {
    countries: [],
    categories: [],
    languages: [],
    aggregators: [],
  };

  UNSAFE_componentWillMount() {
    /* istanbul ignore if */
    if (
      isEmpty(this.props.selectedCategories) &&
      isEmpty(this.props.selectedCountry) &&
      isEmpty(this.props.selectedLanguage)
    ) {
      this.props.initializeCategory();
      this.props.initializeCountry();
      this.props.initializeLanguage();
    }

    if (this.props.shouldShowAggregators && isEmpty(this.props.selectedAggregator))
      this.props.initializeAggregator();
  }

  componentDidMount() {
    // @ts-ignore
    trackEvent({ category: 'page-type', action: 'view', label: 'submit-metadata' });
  }

  get countryProps() {
    const { selectedCountry, errors, onChange } = this.props;
    return {
      id: 'country',
      'data-testid': 'country',
      onChange: onChange('country'),
      value: selectedCountry && selectedCountry.code ? JSON.stringify(selectedCountry) : '',
      error: errors && errors.country,
    };
  }

  get languageProps() {
    const { selectedLanguage, errors, onChange } = this.props;
    return {
      id: 'language',
      'data-testid': 'language',
      onChange: onChange('language'),
      value: selectedLanguage && selectedLanguage.code ? JSON.stringify(selectedLanguage) : '',
      error: errors && errors.language,
    };
  }

  get aggregatorProps() {
    const { selectedAggregator, errors, onChange } = this.props;
    return {
      id: 'aggregator',
      'data-testid': 'aggregator',
      onChange: onChange('aggregator'),
      value: selectedAggregator && selectedAggregator.id ? JSON.stringify(selectedAggregator) : '',
      error: errors && errors.aggregator,
    };
  }

  get highlightedCountries() {
    return filter(this.props.countries, c => ['US', 'SE', 'DE', 'GB'].includes(c.code));
  }

  get highlightedLanguages() {
    return filter(this.props.languages, c => ['en', 'de', 'sv'].includes(c.code));
  }

  get knownAggregators() {
    return filter(this.props.aggregators, a => a.id !== DEFAULT_SPOTIFY_AGGREGATOR_ID);
  }

  get defaultSpotifyNetwork(): Network | undefined {
    return filter(this.props.aggregators, a => a.id === DEFAULT_SPOTIFY_AGGREGATOR_ID)[0];
  }

  render() {
    const {
      categories,
      errors,
      initializeCategory,
      setCategory,
      setSubcategory,
      removeCategory,
      removeSubcategory,
    } = this.props;

    const selectedCategories = this.props.selectedCategories;

    if (!selectedCategories || isEmpty(selectedCategories)) {
      return null;
    }

    const selectMessages = defineMessages({
      selectCountry: {
        id: 'add-podcast.additional-info-select-country',
        defaultMessage: 'Select country',
      },
      selectLanguage: {
        id: 'add-podcast.additional-info-select-language',
        defaultMessage: 'Select language',
      },
      selectHostingProvider: {
        id: 'add-podcast.additional-info-select-hosting-provider',
        defaultMessage: 'Select hosting provider',
      },
    });

    return (
      <PodcastModalBody data-qa="additional-info-form">
        <Type as="h1" variant={Type.heading2}>
          <FormattedMessage
            id="add-podcast.additional-info-title"
            defaultMessage="Tell us more about your podcast"
          />
        </Type>
        <Type as="p" variant={Type.body1}>
          <FormattedMessage
            id="add-podcast.additional-info-instructions"
            defaultMessage="This will help more podcast listeners on Spotify discover your episodes."
          />
        </Type>
        <FormGroup
          label={
            <FormattedMessage
              id="add-podcast.additional-info-country-form-title"
              defaultMessage="Where your podcast is made"
            />
          }
          labelFor="country"
          indicator="required"
        >
          <FormSelect {...this.countryProps}>
            <option value="" disabled>
              {this.props.intl.formatMessage(selectMessages.selectCountry)}
            </option>
            {this.highlightedCountries.map(c => (
              <option key={c.code} value={JSON.stringify(c)}>
                {c.name}
              </option>
            ))}
            <option disabled>────────</option>
            {this.props.countries.map(c => (
              <option key={c.code} value={JSON.stringify(c)}>
                {c.name}
              </option>
            ))}
          </FormSelect>
          {this.countryProps.error && (
            <FormHelpText error>
              <FormattedMessage
                id="add-podcast.additional-info-country-missing"
                defaultMessage="Please select a country."
                description="Error text to display when the user has not selected a country"
              />
            </FormHelpText>
          )}
        </FormGroup>

        <FormGroup
          label={
            <FormattedMessage
              id="add-podcast.additional-info-language-form-title"
              defaultMessage="Primary language spoken in your podcast"
            />
          }
          labelFor="language"
          indicator="required"
        >
          <FormSelect {...this.languageProps}>
            <option value="" disabled>
              {this.props.intl.formatMessage(selectMessages.selectLanguage)}
            </option>
            {this.highlightedLanguages.map(l => (
              <option key={l.code} value={JSON.stringify(l)}>
                {l.name}
              </option>
            ))}
            <option disabled>────────</option>
            {this.props.languages.map(l => (
              <option key={l.code} value={JSON.stringify(l)}>
                {l.name}
              </option>
            ))}
          </FormSelect>
          {this.languageProps.error && (
            <FormHelpText error>
              <FormattedMessage
                id="add-podcast.additional-info-language-missing"
                description="Error text to display when the user has not selected a language"
                defaultMessage="Please select a language."
              />
            </FormHelpText>
          )}
        </FormGroup>

        {this.props.shouldShowAggregators && (
          <FormGroup
            label={
              <FormattedMessage
                id="add-podcast.additional-info-hosting-form-title"
                defaultMessage="Who is your hosting provider?"
              />
            }
            labelFor="aggregator"
            indicator="required"
          >
            <FormSelect {...this.aggregatorProps}>
              <option value="" disabled>
                {this.props.intl.formatMessage(selectMessages.selectHostingProvider)}
              </option>
              {this.knownAggregators.map(l => (
                <option key={l.id} value={JSON.stringify(l)}>
                  {l.name}
                </option>
              ))}
              <option disabled>────────</option>

              {this.defaultSpotifyNetwork && (
                <option
                  key={this.defaultSpotifyNetwork.id}
                  value={JSON.stringify(this.defaultSpotifyNetwork)}
                >
                  {this.defaultSpotifyNetwork.name}
                </option>
              )}
            </FormSelect>
            {this.aggregatorProps.error && (
              <FormHelpText error>
                <FormattedMessage
                  id="add-podcast.additional-info-hosting-provider-missing"
                  description="Error text to display when the user has not selected a hosting provider"
                  defaultMessage="Please select your hosting provider"
                />
              </FormHelpText>
            )}
          </FormGroup>
        )}

        {selectedCategories.map((category, idx) => (
          <Category
            key={category.categoryKey}
            category={category}
            setCategory={setCategory}
            removeCategory={removeCategory}
            setSubcategory={setSubcategory}
            removeSubcategory={removeSubcategory}
            categories={categories}
            error={errors && errors.category && errors.category[idx]}
            intl={this.props.intl}
          />
        ))}

        {/**
         * Show only if there is at lease one selected category, and there are less than 3 categories
         */
        get(selectedCategories, '0.value', false) && selectedCategories.length < 3 && (
          <FormGroup>
            <AdditionalCategoryButton
              data-qa="additional-category-button"
              onClick={() => {
                const categoryKey = `category${selectedCategories.length + 1}`;
                initializeCategory(categoryKey);
              }}
            >
              <IconPlusAlt aria-label="IconPlusAlt" iconSize={32} />
              <Type as="p" condensed>
                {' '}
                <FormattedMessage
                  id="add-podcast.additional-info-add-category"
                  description="Button text to add an additoinal category"
                  defaultMessage="Add an additional category"
                />{' '}
              </Type>
            </AdditionalCategoryButton>
          </FormGroup>
        )}
      </PodcastModalBody>
    );
  }
}
